import type { CardId } from '@rummy/engine';
import { useEffect, useState } from 'react';
import { PlayingCard } from './PlayingCard.tsx';

export interface Flight {
  id: number;
  card: CardId | null;
  wild: boolean;
  from: { x: number; y: number };
  to: { x: number; y: number };
}

/** How long a card takes to cross the table. Within §6.5's motion budget. */
export const FLIGHT_MS = 420;

/**
 * A card crossing the table from where it was taken to whoever took it.
 *
 * With four or five seats it is otherwise genuinely hard to tell who just drew,
 * and whether they took the open card — which is the single most informative
 * thing that happens on someone else's turn. A count ticking up in a seat card
 * does not communicate it; a card physically moving there does.
 *
 * A card off the closed deck flies face down, because that is exactly what the
 * table saw. The projection has already decided what the viewer may know
 * (§5) — this only animates it.
 */
export function CardFlights({ flights }: { flights: Flight[] }) {
  if (flights.length === 0) return null;
  return (
    <div className="flights" aria-hidden="true">
      {flights.map((flight) => (
        <FlyingCard key={flight.id} flight={flight} />
      ))}
    </div>
  );
}

function FlyingCard({ flight }: { flight: Flight }) {
  const [arrived, setArrived] = useState(false);

  useEffect(() => {
    // Two frames: one to paint at the origin, one to start the transition.
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setArrived(true));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, []);

  const at = arrived ? flight.to : flight.from;
  /*
   * A thrown card turns in the air and lands askew — which now matters, because
   * the open pile is a scattered stack rather than one squared card.
   *
   * Derived from the flight id rather than randomised: a re-render mid-flight
   * must not change the angle, or the card visibly jerks.
   */
  const spin = ((flight.id % 7) - 3) * 3.5;

  return (
    /*
     * Horizontal and vertical are split across two elements so they can carry
     * different easings — linear across, eased down — which is what makes the
     * path an arc rather than a straight diagonal. One transform cannot express
     * that; two nested ones can, and both stay on the compositor.
     */
    <div
      className={`flights__card${arrived ? ' is-arrived' : ''}`}
      style={{
        transform: `translate3d(${at.x}px, 0, 0)`,
        transitionDuration: `${FLIGHT_MS}ms`,
      }}
    >
      <div
        className="flights__lift"
        style={{
          transform: `translate3d(0, ${at.y}px, 0) rotate(${arrived ? spin : 0}deg) scale(${arrived ? 0.62 : 1})`,
          transitionDuration: `${FLIGHT_MS}ms`,
        }}
      >
        <PlayingCard
          id={flight.card ?? 'JK:0'}
          faceDown={flight.card === null}
          wild={flight.wild}
        />
      </div>
    </div>
  );
}
